import { ServiceItem, ServiceTransaction } from '../types';
import { altCoveredQuantities } from './externalAdvances';
import { bestandteilSperrText, heldByComponentsQuantity, openQuantity, stampFlatCoverage } from './projectLv';
import { ABGESCHLOSSEN, istAusgeglichen } from './unbookedReportsLogic';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * DER ARCHIV-RIEGEL — zwei Regeln, ein Urteil
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Betreiberentscheidung vom 02.09.2026, zwei Teile:
 *
 *   (1) Ein Mutterprojekt darf erst archiviert werden, wenn auch seine
 *       Toechter abgerechnet und archivreif sind.
 *   (2) KEIN Projekt — Mutter, Tochter oder alleinstehend — darf ins Archiv,
 *       solange es selbst erbrachte, aber nicht abgerechnete Leistung traegt.
 *
 * Teil (2) ist der juengere und der wichtigere. Teil (1) allein liess sich
 * umgehen: Tochter trennen, dann Mutter archivieren. Verschaerfend haelt
 * `planLinkReturn` beim Trennen genau die Positionen MIT HISTORIE an der
 * Mutter zurueck — danach traegt die Mutter die unabgerechnete Leistung der
 * ehemaligen Tochter und waere ohne Teil (2) trotzdem archivierbar.
 *
 * Der Betreiber kennt und will die Breite dieser Regel: im Bestand fuehren
 * abgeschlossene Projekte wie 5983 und 6136 zusammen rund 1.280 EUR als offen,
 * ohne dass es jemand sieht (die Begruendung dazu steht bei `meldetNoch` in
 * unbookedReportsLogic — ein archiviertes, ausgeglichenes Projekt hoert auf zu
 * melden, ein archiviertes mit offenem Posten nicht).
 *
 * ─── Der Ausweg, und warum es genau EINER sein muss ────────────────────────
 *
 * Wer bewusst nichts mehr fordern will, nutzt den VERZICHT
 * (`quantityWaived`) — er ist genau dafuer gebaut und verlangt eine
 * Begruendung. Der Riegel nennt ihn in der Meldung ausdruecklich, sonst waere
 * er eine Sackgasse: „geht nicht" ohne den Weg daneben fuehrt dazu, dass
 * jemand die Menge stattdessen zurueckbucht und damit den Leistungsnachweis
 * faelscht (die Abgrenzung Verzicht/Ruecknahme steht bei
 * ServiceItem.quantityWaived).
 *
 * ─── WELCHE GROESSE „OFFEN" IST: `displayOpen`, nicht `open` ───────────────
 *
 * `getProjectFinancials` (projectLv.ts) fuehrt beide, und die Wahl ist hier
 * teuer. Der Unterschied ist genau eine Menge: was eine ALT-RECHNUNG bereits
 * gefordert hat (`ALT-`-Zeilen im Protokoll, `altCoveredQuantities`).
 *
 *   `open`        zaehlt sie MIT. Nachgetragene Alt-Leistungen tragen
 *                 `quantityBilled = 0` und muessen das auch: abgerechnet wird
 *                 der Gesamtstand von der kumulativen Rechnung, deren Abzug
 *                 die Alt-Abschlaege enthaelt.
 *   `displayOpen` zieht sie ab.
 *
 * Gewaehlt ist `displayOpen`, und zwar aus einem Grund, der nur fuer einen
 * RIEGEL gilt: `open` faellt an so einer Position NIE wieder. Ein Projekt, das
 * ins Archiv soll, bekommt per Definition keine kumulative Rechnung mehr —
 * `quantityBilled` bleibt also fuer immer 0, und der Riegel spraeche ein
 * dauerhaftes Nein. Der einzige Ausweg waere dann ein Verzicht auf eine Menge,
 * die sehr wohl gefordert (und meist bezahlt) wurde: eine Falschaussage im
 * Beleg, erzwungen von einer Wache. Ein Riegel, dessen einziger Ausgang durch
 * eine Faelschung fuehrt, ist schlechter als kein Riegel.
 *
 * Die andere Richtung ist hier vertretbar: die Alt-Deckung ist KEINE Schaetzung.
 * Sie entsteht ausschliesslich aus `ALT-`-Buchungen, die „Alt-Rechnung
 * einlesen" bewusst und mit einem Beleg in der Hand schreibt. Und sie macht
 * nichts unsichtbar: die Buchhaltungsansicht, der Projektcheck und
 * `v_unbooked_sent_reports` rechnen weiter mit `open` — ein Projekt, das
 * dieser Riegel wegen Alt-Deckung durchlaesst, steht dort unveraendert in der
 * Liste. Der Riegel ist die letzte Tuer, nicht das letzte Netz.
 *
 * MONATSPAUSCHALE: eine von einer Pauschale gedeckte Position (`flatCoveredBy`)
 * traegt kein offenes Geld und sperrt deshalb nicht. Das steht nicht eigens
 * hier, sondern kommt aus `openQuantity` — dieselbe Regel, aus der auch
 * Rechnungsauswahl, Projektcheck und Rechnungslauf lesen. Eine zweite Wahrheit
 * dafuer waere genau die Doppelregel, an der dieses Modul schon einmal
 * auseinandergelaufen ist.
 *
 * NICHT benutzt wird `istAusgeglichen` fuer den eigenen Stand: es kennt weder
 * die Pauschale noch die Alt-Deckung. Fuer die TOECHTER bleibt es die
 * Grundlage (siehe unten) — dort ist es der Bestand, und die Zahlen einer
 * Tochter kommen ohnehin roh aus `project_services`.
 *
 * ─── Bestandsprojekte, die schon im Archiv liegen ──────────────────────────
 *
 * Der Riegel greift ausschliesslich beim WECHSEL nach `finished` — beide
 * Einstiege in der Oberflaeche kurzschliessen vorher (`ProjectStats`:
 * `if (newStatus === globalData.status) return;`), und `withAutoArchive`
 * laeuft nur ueber `autoArchiveVerdict`, das ein bereits archiviertes Projekt
 * gar nicht erst vorschlaegt. Ein Projekt, das heute mit offenem Posten im
 * Archiv liegt, bleibt also unveraendert bedienbar: es laesst sich oeffnen,
 * abrechnen, wieder aktiv setzen und verzichten. Erst wenn es danach ERNEUT
 * archiviert werden soll, spricht der Riegel — und dann zu Recht.
 *
 * ─── Teil (1): eine Mutter mit offenen Toechtern ───────────────────────────
 *
 * ─── Warum das Geld kostet ─────────────────────────────────────────────────
 *
 * Eine Tochter hat nach dem Verknuepfen KEIN eigenes Leistungsverzeichnis
 * mehr: `planLinkTransfer` (projectLv.ts) haengt beim Verknuepfen jede
 * Position an die MUTTER um — teils verschmolzen, teils als eigene Zeile,
 * aber immer dort. Gebucht wird seither in das Protokoll der Mutter, und
 * abgerechnet wird ueber deren LV.
 *
 * Wandert die Mutter ins Archiv, waehrend die Tochter noch laeuft, passiert
 * zweierlei, und beides lautlos:
 *   • Die Mutter hoert auf zu melden. Ein Projekt wird still, sobald es
 *     `finished` ist UND nichts mehr offen hat (unbookedReportsLogic,
 *     `meldetNoch`). Solange die Tochter noch Leistungen einbucht, kippt der
 *     Stand hin und her — und in jedem Moment, in dem gerade nichts offen
 *     steht, verschwindet das Projekt aus den Arbeitslisten.
 *   • Die Tochter bucht weiter auf ein Projekt, das niemand mehr ansieht.
 *
 * Genau diese Verlustklasse ist im Bestand mehrfach belegt: erbrachte, nie
 * abgerechnete Begehungen, die erst Monate spaeter auffielen.
 *
 * ─── Woran sich „offen" bei einer Tochter festmacht ────────────────────────
 *
 * Der feine Punkt, und die Antwort ist zweigeteilt:
 *
 * (1) TRAGEND IST DER STATUS. Weil das LV an der Mutter liegt, kann eine
 *     sauber verknuepfte Tochter regelmaessig gar KEIN eigenes offenes Geld
 *     tragen — ihre Positionsliste ist leer, und `istAusgeglichen([])` ist
 *     wahr. Ihr offener Posten steht als offener Posten DER MUTTER da. Was
 *     die Tochter allein aussagt, ist: „hier wird noch gearbeitet". Deshalb
 *     ist „nicht `finished`" der Hauptgrund, an dem dieser Riegel haelt.
 *
 * (2) DIE GELDPRUEFUNG BLEIBT TROTZDEM DRIN, weil (1) nur fuer den sauberen
 *     Fall gilt. Vor dem Umhaengen verknuepfte Toechter tragen ihr altes LV
 *     bis heute in `project_services` weiter — unsichtbar, weil die Ansicht
 *     das LV der Mutter aufloest, aber vorhanden (im Bestand von Hand
 *     aufgeloest bei 26071018/5606). Genauso kann eine Position von Hand
 *     zurueckgewandert sein. Eine Tochter, die eine solche Zeile mit
 *     erbrachter, nicht abgerechneter Menge fuehrt, traegt sehr wohl eigenes
 *     Geld. Die Pruefung kostet eine Abfrage und schliesst genau die Luecke,
 *     die niemand sieht.
 *
 * „Ausgeglichen" wird NICHT neu erfunden: es ist `istAusgeglichen` aus
 * `unbookedReportsLogic` — dieselbe Vorstellung, die auch `ladeStilleProjekte`
 * benutzt. Der Verzicht (`quantityWaived`) zaehlt damit als ausgeglichen, eine
 * nur erbrachte, nicht abgerechnete Menge nicht.
 *
 * ─── Die sichere Richtung ──────────────────────────────────────────────────
 *
 * Jede Wissensluecke SPERRT. Faellt eine Abfrage aus, fehlt ein Recht oder
 * widersprechen sich die beiden Statusquellen, ist das Urteil „nicht
 * feststellbar" — und nicht feststellbar heisst nein. Dieselbe Richtung wie
 * bei `countLinkedChildren`, wo `null` ebenfalls sperrt: eine Archivierung
 * laesst sich nachholen, eine uebersehene Forderung nicht.
 *
 * ─── Warum nur EINE Ebene geprueft wird — und trotzdem keine Kette durchkommt
 *
 * Der Bestand schliesst Enkel aus (eine Tochter kann selbst keine Toechter
 * mehr bekommen), aber darauf verlaesst sich dieser Riegel nicht. Statt in
 * die Tiefe zu laufen, gilt: eine Tochter, die SELBST Toechter hat, haelt die
 * Mutter auf (`eigene_toechter`). Damit ist die Kette vollstaendig abgedeckt,
 * ohne sie zu durchlaufen — durchgelassen wird eine Mutter nur, wenn jede
 * Tochter archivreif UND kinderlos ist, und eine Kette wird von unten nach
 * oben abgearbeitet. Das ist strenger als noetig (eine archivreife Tochter
 * mit archivreifer Enkelin blockiert ebenfalls), kostet aber nur einen Klick
 * mehr und spart je Ebene eine Abfragerunde.
 */

/** Warum etwas der Archivierung im Weg steht. */
export type ArchivBlockGrund =
    /** Die Tochter steht nicht im Archiv — dort wird noch gearbeitet. */
    | 'laeuft_noch'
    /** Die Tochter ist archiviert, fuehrt aber noch nicht abgerechnete Leistung. */
    | 'offener_posten'
    /** An der Tochter haengen eigene Toechter; die Kette wird von unten geloest. */
    | 'eigene_toechter'
    /** Der Zustand liess sich nicht feststellen (Abfrage, Rechte, Widerspruch). */
    | 'unbekannt'
    /**
     * DAS PROJEKT SELBST traegt erbrachte, nicht abgerechnete Leistung.
     * Entscheidung 2 (siehe Modulkopf) — gilt fuer jedes Projekt, nicht nur
     * fuer Muetter.
     */
    | 'eigener_posten'
    /** Der eigene Abrechnungsstand liess sich nicht feststellen. */
    | 'eigener_stand_unbekannt'
    /**
     * REGEL 3 (Betreiberentscheidung vom 15.09.2026): ins Archiv geht ein
     * Projekt erst, wenn die Schlussrechnung GESTELLT und BEZAHLT ist. Bis
     * dahin steht es auf BEENDET ('completed') und in der Buchhaltung als
     * faellig fuer die Schlussrechnung.
     */
    /** Es gibt Belege zum Projekt, aber keine gestellte Schlussrechnung. */
    | 'schlussrechnung_fehlt'
    /** Die Schlussrechnung ist gestellt, aber noch nicht bezahlt. */
    | 'schlussrechnung_unbezahlt'
    /** Ein anderer gestellter Beleg des Projekts ist noch nicht erledigt. */
    | 'beleg_offen'
    /** Die Belege liessen sich nicht laden. */
    | 'belegstand_unbekannt';

/**
 * Der Zustand EINER Tochter, so wie ihn der Lader beschafft.
 *
 * Jedes Feld darf fehlen. Fehlt es, ist die Aussage „nicht feststellbar" — und
 * die sperrt. Das ist Absicht: ein Aufrufer, der ein Feld nicht fuellen kann,
 * soll nicht versehentlich durchlassen.
 */
export interface TochterZustand {
    projectId: string;
    projectNumber?: string | null;
    name?: string | null;
    /** `projects.status`. */
    statusSpalte?: string | null;
    /** `data->metadata->globalData->status` — die zweite Quelle desselben Werts. */
    statusJson?: string | null;
    /**
     * Ist an der Tochter selbst nichts mehr abzurechnen (`istAusgeglichen`
     * ueber ihre eigenen Positionen)? `null`/fehlend = nicht feststellbar.
     */
    ausgeglichen?: boolean | null;
    /** Wie viele eigene Positionen noch offen sind — nur fuer die Meldung. */
    offenePositionen?: number | null;
    /**
     * Anzahl der eigenen Toechter dieser Tochter. `null`/fehlend = nicht
     * feststellbar (siehe `countLinkedChildren`).
     */
    eigeneToechter?: number | null;
}

/** Etwas, das im Weg steht — eine Tochter oder eine eigene Position. */
export interface ArchivBlocker {
    /** Bei einem eigenen Posten leer: der Blocker steckt im Projekt selbst. */
    projectId: string;
    projectNumber?: string | null;
    name?: string | null;
    grund: ArchivBlockGrund;
    /** Nur bei `eigener_posten`: die LV-Position, die noch offen ist. */
    serviceId?: string;
    /** Nur bei den Beleg-Gruenden (Regel 3): der Beleg, um den es geht. */
    belegId?: string;
    /** Klartext fuer die Liste in der Meldung. */
    text: string;
}

export interface ArchivUrteil {
    /** Darf die Mutter ins Archiv? */
    erlaubt: boolean;
    /** Die Toechter, die im Weg stehen — beim Namen genannt. */
    blocker: ArchivBlocker[];
    /** Fertige, handlungsfaehige Meldung. Leer, wenn erlaubt. */
    meldung: string;
}

/** Statusnamen wie in der Oberflaeche (ProjectStats, AdminProjectList). */
const STATUS_LABEL: Record<string, string> = {
    planning: 'Planung',
    active: 'Ausführung',
    on_hold: 'Ruht',
    completed: 'Beendet',
    [ABGESCHLOSSEN]: 'Archiv',
};

const gelesen = (wert: string | null | undefined): wert is string =>
    typeof wert === 'string' && wert.trim() !== '';

/**
 * Der Status der Tochter aus BEIDEN Quellen.
 *
 * `saveProjectToCloud` leitet die Spalte `projects.status` bei jedem Speichern
 * aus `globalData.status` ab; im Regelfall sagen beide dasselbe. Wo sie es
 * nicht tun, ist nicht entscheidbar, welche gilt — und „nicht entscheidbar"
 * sperrt. Eine FEHLENDE zweite Quelle ist dagegen kein Widerspruch: dann
 * entscheidet die vorhandene allein, sonst blockierte jede Altzeile ohne
 * JSON-Pfad grundlos.
 */
const effektiverStatus = (t: TochterZustand): string | undefined => {
    const spalte = gelesen(t.statusSpalte) ? t.statusSpalte.trim() : undefined;
    const json = gelesen(t.statusJson) ? t.statusJson.trim() : undefined;
    if (!spalte && !json) return undefined;
    if (spalte && json && spalte !== json) return undefined;
    return spalte || json;
};

/**
 * Muss der Lader fuer diese Tochter noch die Enkel-Frage stellen?
 *
 * Die Frage kostet je Tochter eine eigene Abfrage (`countLinkedChildren`) und
 * lohnt nur, wo sie ueberhaupt noch etwas entscheiden kann: eine Tochter, die
 * schon am Status oder am Abrechnungsstand haengt, blockiert ohnehin.
 *
 * Sie steht HIER und nicht im Lader, weil sie dieselbe Statusregel benutzen
 * MUSS wie `beurteileTochter` (`effektiverStatus`: eine einzelne Quelle
 * genuegt, nur ein Widerspruch sperrt). Eine strengere Fassung im Lader —
 * etwa „beide Quellen muessen woertlich 'finished' sagen" — liesse
 * `eigeneToechter` genau dort ungefuellt, wo die Regel die Tochter sonst
 * durchgelassen haette, und jede Altzeile ohne JSON-Status blockierte
 * grundlos als „unbekannt".
 */
export const brauchtEnkelfrage = (t: TochterZustand): boolean =>
    effektiverStatus(t) === ABGESCHLOSSEN && t.ausgeglichen === true;

const bezeichnung = (t: { projectNumber?: string | null; name?: string | null }): string => {
    const nummer = (t.projectNumber || '').trim();
    const name = (t.name || '').trim();
    if (nummer && name) return `${nummer} – ${name}`;
    return nummer || name || 'Unbenanntes Tochterprojekt';
};

/**
 * Beurteilt EINE Tochter. `undefined` heisst: sie steht nicht im Weg.
 *
 * Die Reihenfolge der Pruefungen ist die Reihenfolge, in der der Anwender die
 * Tochter abarbeiten muss — erst fertig arbeiten, dann abrechnen, dann die
 * eigene Kette aufloesen.
 */
const beurteileTochter = (t: TochterZustand): ArchivBlocker | undefined => {
    const kopf = bezeichnung(t);
    const blocker = (grund: ArchivBlockGrund, text: string): ArchivBlocker => ({
        projectId: t.projectId,
        projectNumber: t.projectNumber ?? null,
        name: t.name ?? null,
        grund,
        text: `${kopf} — ${text}`,
    });

    const status = effektiverStatus(t);
    if (!status) {
        return blocker('unbekannt', 'Status nicht feststellbar (keine oder widersprüchliche Angabe).');
    }
    if (status !== ABGESCHLOSSEN) {
        return blocker('laeuft_noch', `läuft noch (Status: ${STATUS_LABEL[status] || status}).`);
    }

    if (t.ausgeglichen === null || t.ausgeglichen === undefined) {
        return blocker('unbekannt', 'im Archiv, aber der Abrechnungsstand ist nicht feststellbar.');
    }
    if (!t.ausgeglichen) {
        const anzahl = t.offenePositionen ?? 0;
        const menge = anzahl > 0
            ? `${anzahl} Position${anzahl === 1 ? '' : 'en'} erbracht und nicht abgerechnet`
            : 'noch erbrachte, nicht abgerechnete Leistung';
        return blocker('offener_posten', `im Archiv, trägt aber ${menge}.`);
    }

    if (t.eigeneToechter === null || t.eigeneToechter === undefined) {
        return blocker('unbekannt', 'es ist nicht feststellbar, ob eigene Tochterprojekte daran hängen.');
    }
    if (t.eigeneToechter > 0) {
        return blocker(
            'eigene_toechter',
            `hat selbst ${t.eigeneToechter} Tochterprojekt${t.eigeneToechter === 1 ? '' : 'e'} — diese zuerst abschließen.`,
        );
    }

    return undefined;
};

/**
 * DIE ENTSCHEIDUNG: darf die Mutter ins Archiv?
 *
 * `toechter === null` heisst „der Zustand der Toechter liess sich ueberhaupt
 * nicht feststellen" und sperrt — der Lader gibt das zurueck, wenn die
 * Abfrage scheitert oder ein Recht fehlt.
 *
 * Eine LEERE Liste heisst „es gibt keine Toechter" und erlaubt. Der
 * Unterschied zwischen „keine" und „nicht feststellbar" ist der ganze Kern
 * dieser Signatur; ein Boolean oder ein leeres Array fuer beides waere genau
 * die Verwechslung, die eine Forderung verschwinden laesst.
 */
export const beurteileToechter = (
    toechter: TochterZustand[] | null | undefined,
): ArchivUrteil => {
    if (!toechter) {
        const blocker: ArchivBlocker[] = [{
            projectId: '',
            projectNumber: null,
            name: null,
            grund: 'unbekannt',
            text: 'Die Tochterprojekte konnten nicht geladen werden.',
        }];
        return {
            erlaubt: false,
            blocker,
            meldung: baueMeldung(blocker),
        };
    }

    const blocker = toechter
        .map(beurteileTochter)
        .filter((b): b is ArchivBlocker => !!b);

    if (blocker.length === 0) return { erlaubt: true, blocker: [], meldung: '' };
    return { erlaubt: false, blocker, meldung: baueMeldung(blocker) };
};

/** Steckt dieser Blocker im Projekt SELBST (und nicht in einer Tochter)? */
const istEigenerBlocker = (b: ArchivBlocker): boolean =>
    b.grund === 'eigener_posten' || b.grund === 'eigener_stand_unbekannt';

/** Geht es um die Belege (Regel 3)? */
const istBelegBlocker = (b: ArchivBlocker): boolean =>
    b.grund === 'schlussrechnung_fehlt'
    || b.grund === 'schlussrechnung_unbezahlt'
    || b.grund === 'beleg_offen'
    || b.grund === 'belegstand_unbekannt';

/** Der Abschnitt zu den Belegen — Schlussrechnung gestellt und bezahlt? */
const abschnittBelege = (blocker: ArchivBlocker[], allein: boolean): string => {
    const unklar = blocker.every((b) => b.grund === 'belegstand_unbekannt');
    const kopf = unklar
        ? (allein
            ? 'Das Projekt wurde NICHT archiviert: seine Belege ließen sich nicht prüfen.'
            : 'Die Belege ließen sich nicht prüfen:')
        : (allein
            ? 'Das Projekt wurde NICHT archiviert: die Schlussrechnung ist noch nicht gestellt und bezahlt.'
            : 'Belege, die dem Archiv im Weg stehen:');
    const liste = blocker.map((b) => `• ${b.text}`).join('\n');
    const fuss = unklar
        ? 'Bitte erneut versuchen. Solange der Stand unklar ist, bleibt das Projekt offen — eine übersehene Forderung fällt niemandem auf.'
        : 'Ins Archiv geht ein Projekt erst, wenn die Schlussrechnung gestellt UND bezahlt ist. Bis dahin bleibt es auf „Beendet" und steht in der Buchhaltung als fällig für die Schlussrechnung.';
    return `${kopf}\n\n${liste}\n\n${fuss}`;
};

/** Der Abschnitt zu den eigenen offenen Posten. */
const abschnittEigene = (blocker: ArchivBlocker[], allein: boolean): string => {
    const unklar = blocker.every((b) => b.grund === 'eigener_stand_unbekannt');
    const kopf = unklar
        ? (allein
            ? 'Das Projekt wurde NICHT archiviert: sein Abrechnungsstand ließ sich nicht prüfen.'
            : 'Der eigene Abrechnungsstand ließ sich nicht prüfen:')
        : (allein
            ? `Das Projekt wurde NICHT archiviert: ${blocker.length === 1 ? 'eine Position ist' : `${blocker.length} Positionen sind`} erbracht, aber nicht abgerechnet.`
            : `Offene Posten in diesem Projekt (${blocker.length === 1 ? 'eine Position' : `${blocker.length} Positionen`}):`);
    const liste = blocker.map((b) => `• ${b.text}`).join('\n');
    const fuss = unklar
        ? 'Bitte erneut versuchen. Solange der Stand unklar ist, bleibt das Projekt offen — eine übersehene Forderung fällt niemandem auf.'
        : `${blocker.length === 1 ? 'Diese Position' : 'Diese Positionen'} bitte abrechnen. Soll dafür nichts mehr gefordert werden, schließt der VERZICHT sie mit Begründung ab (Buchhaltung → Verzicht); erst danach geht das Projekt ins Archiv.`;
    return `${kopf}\n\n${liste}\n\n${fuss}`;
};

/** Der Abschnitt zu den Toechtern — Wortlaut unveraendert seit Teil (1). */
const abschnittToechter = (blocker: ArchivBlocker[], allein: boolean): string => {
    const nichtFeststellbar = blocker.every((b) => b.grund === 'unbekannt');
    const kopf = nichtFeststellbar
        ? (allein
            ? 'Das Projekt wurde NICHT archiviert: der Zustand seiner Tochterprojekte ließ sich nicht prüfen.'
            : 'Der Zustand der Tochterprojekte ließ sich nicht prüfen:')
        : (allein
            ? `Das Projekt wurde NICHT archiviert: ${blocker.length === 1 ? 'ein Tochterprojekt ist' : `${blocker.length} Tochterprojekte sind`} noch nicht archivreif.`
            : `Tochterprojekte, die noch nicht archivreif sind (${blocker.length === 1 ? 'ein Tochterprojekt' : `${blocker.length} Tochterprojekte`}):`);
    const liste = blocker.map((b) => `• ${b.text}`).join('\n');
    const fuss = nichtFeststellbar
        ? 'Bitte erneut versuchen. Solange der Stand unklar ist, bleibt das Projekt offen — eine übersehene Forderung fällt niemandem auf.'
        : 'Bitte diese Projekte zuerst abrechnen und archivieren. Danach kann das Mutterprojekt geschlossen werden.';
    return `${kopf}\n\n${liste}\n\n${fuss}`;
};

/**
 * Die Meldung fuer den Anwender.
 *
 * Sie nennt jeden Blocker beim Namen: die Tochter mit Nummer, Namen und Grund,
 * die eigene Position mit LV-Nummer, Titel und offener Menge. „Nicht moeglich"
 * allein waere hier die schlechteste Auskunft — die Toechter sieht der
 * Anwender nirgends gebuendelt, und welche seiner LV-Positionen noch offen
 * steht, muesste er sonst in der Buchhaltung suchen.
 *
 * Liegen BEIDE Gruende vor, werden auch beide genannt. Nur den ersten zu
 * melden hiesse, den Anwender zweimal gegen dieselbe Wand laufen zu lassen:
 * er raeumt die Toechter ab, klickt erneut — und bekommt die naechste
 * Absage. Deshalb zwei Abschnitte in EINER Meldung, in der Reihenfolge, in der
 * er sie abarbeiten kann (das Eigene zuerst, es liegt in seiner Hand).
 */
export const baueMeldung = (blocker: ArchivBlocker[]): string => {
    if (blocker.length === 0) return '';
    const eigene = blocker.filter(istEigenerBlocker);
    const belege = blocker.filter(istBelegBlocker);
    const kinder = blocker.filter((b) => !istEigenerBlocker(b) && !istBelegBlocker(b));
    // Reihenfolge der Arbeit: das Eigene (abrechnen), dann die Belege (stellen,
    // bezahlen lassen), dann die Toechter (Projekt wechseln).
    const abschnitte: Array<[ArchivBlocker[], (b: ArchivBlocker[], allein: boolean) => string]> = [
        [eigene, abschnittEigene],
        [belege, abschnittBelege],
        [kinder, abschnittToechter],
    ];
    const vorhanden = abschnitte.filter(([liste]) => liste.length > 0);
    if (vorhanden.length === 1) return vorhanden[0][1](vorhanden[0][0], true);
    const teile: string[] = [
        `Das Projekt wurde NICHT archiviert — es gibt ${vorhanden.length === 2 ? 'zwei' : 'drei'} Gründe:`,
    ];
    vorhanden.forEach(([liste, bau]) => { teile.push('', bau(liste, false)); });
    return teile.join('\n');
};

/**
 * Die Positionen einer Tochter zu `ausgeglichen` + `offenePositionen`
 * verdichtet — die Bruecke zwischen der Datenbankzeile und dem Zustand oben.
 *
 * Steht hier und nicht im Lader, damit die Zaehlung mitgeprueft werden kann:
 * die Zahl steht in der Meldung, und eine falsche Zahl schickt den Anwender
 * in die falsche Position.
 */
export const verdichtePositionen = (
    positionen: Array<{ quantityUsed?: number; quantityBilled?: number; quantityWaived?: number }>,
): { ausgeglichen: boolean; offenePositionen: number } => ({
    ausgeglichen: istAusgeglichen(positionen),
    offenePositionen: positionen.filter((p) => !istAusgeglichen([p])).length,
});

// ═══════════════════════════════════════════════════════════════════════════
// TEIL (2): EIN OFFENER POSTEN SPERRT DAS ARCHIV — fuer JEDES Projekt
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Mengenrundung. Wortgleich zu `istAusgeglichen` (unbookedReportsLogic) und
 * zum Projektcheck: eine Position, die um weniger als einen halben
 * Tausendstel danebenliegt, ist ein Rundungsrest und keine Forderung.
 */
const MENGEN_EPS = 0.0005;

/** Eine eigene LV-Position, an der noch etwas offen ist. */
export interface EigenePosition {
    /** `ServiceItem.id` — der Anker, ueber den die Buchhaltung sie wiederfindet. */
    serviceId: string;
    /** LV-Nummer, wie sie in der Buchhaltung und auf der Rechnung steht. */
    lvPosition?: string | null;
    title?: string | null;
    /** Einheit (Stk, h, …) — sie steht in der Meldung, sonst ist die Zahl nackt. */
    unit?: string | null;
    /**
     * Erbracht, nicht abgerechnet, nicht verzichtet, nicht von einer
     * Monatspauschale und nicht von einer Alt-Rechnung gedeckt.
     * Die Begruendung dieser Groesse steht im Modulkopf („displayOpen, nicht
     * open").
     */
    offeneMenge: number;
    /**
     * PAKETPOSITION: die Menge ist nicht abrechenbar, sondern ZURUECKGEHALTEN —
     * die Bestandteile sind noch nicht vollstaendig (oder ihr Stand ist nicht
     * ladbar). Gesetzt steht hier der Grund im Klartext („Bestandteile 4/5"),
     * damit die Meldung nicht „abrechnen" verlangt, wo erst die Bestandteile
     * fehlen. Sperren tut die Position trotzdem: unabgerechnet ist
     * unabgerechnet.
     */
    zurueckgehalten?: string;
}

/**
 * Die ganze Lage, aus der das Urteil entsteht.
 *
 * BEIDE Felder tragen den Unterschied zwischen „nichts da" und „nicht
 * feststellbar", und beide Male sperrt `null`. Ein einzelnes Array fuer beides
 * waere genau die Verwechslung, die eine Forderung verschwinden laesst —
 * dieselbe Ueberlegung wie bei `beurteileToechter`.
 */
export interface ArchivLage {
    /**
     * Die eigenen Positionen mit offener Menge. Leeres Array = geprueft, nichts
     * offen. `null` = nicht feststellbar (Abfrage gescheitert, Recht fehlt).
     */
    eigenePosten: EigenePosition[] | null;
    /** Die Toechter, wie bisher. `null` = nicht feststellbar. */
    toechter: TochterZustand[] | null;
    /**
     * REGEL 3: die Belege des Projekts aus dem Rechnungsmodul. Leeres Array =
     * geprueft, es gibt keine (Altbestand, ausserhalb des Moduls abgerechnet —
     * dann entscheiden Regel 1 und 2 allein). `null` = nicht feststellbar,
     * sperrt. `undefined` = NICHT GEPRUEFT: das ist der Weg des Auto-Abschlusses
     * (`pruefeArchivierbarkeitFuerStand`), der ein Projekt nur auf BEENDET
     * setzt und dafuer keine bezahlte Schlussrechnung braucht.
     */
    belege?: BelegZustand[] | null;
}

/** Ein Beleg des Projekts, so weit der Riegel ihn braucht. */
export interface BelegZustand {
    id: string;
    invoiceNumber?: string | null;
    /** `invoices.document_type` — FINAL_INVOICE ist die Schlussrechnung. */
    documentType?: string | null;
    /** `invoices.status`. */
    status?: string | null;
}

/**
 * Status, in denen ein Beleg GESTELLT ist — er hat das Haus verlassen oder
 * ist wenigstens ausgestellt. Entwuerfe (DRAFT, READY_FOR_REVIEW, APPROVED,
 * ISSUING) zaehlen nicht: eine Schlussrechnung im Entwurf ist keine gestellte.
 */
const BELEG_GESTELLT = new Set([
    'ISSUED', 'SENT', 'PARTIALLY_PAID', 'PAID', 'PARTIALLY_CREDITED', 'CREDITED',
    'REFUND_PENDING', 'REFUNDED', 'DISPUTED', 'OVERDUE',
    'SUPERSEDED_BY_FINAL_INVOICE', 'CANCELLED',
]);

/**
 * Status, in denen an einem gestellten Beleg NICHTS MEHR OFFEN ist. Ein
 * Storno oder ein durch die Schlussrechnung abgeloester Abschlag fordert nichts
 * mehr; eine voll gutgeschriebene oder erstattete Rechnung ebenfalls nicht.
 * PARTIALLY_CREDITED, DISPUTED und OVERDUE bleiben offen — dort steht Geld aus.
 */
const BELEG_ERLEDIGT = new Set(['PAID', 'CREDITED', 'REFUNDED', 'SUPERSEDED_BY_FINAL_INVOICE', 'CANCELLED']);

const BELEG_STATUS_LABEL: Record<string, string> = {
    DRAFT: 'Entwurf',
    READY_FOR_REVIEW: 'zur Prüfung',
    APPROVED: 'freigegeben',
    ISSUING: 'wird ausgestellt',
    ISSUED: 'ausgestellt',
    SENT: 'versendet',
    PARTIALLY_PAID: 'teilweise bezahlt',
    PAID: 'bezahlt',
    PARTIALLY_CREDITED: 'teilweise gutgeschrieben',
    CREDITED: 'gutgeschrieben',
    REFUND_PENDING: 'Erstattung offen',
    REFUNDED: 'erstattet',
    DISPUTED: 'strittig',
    OVERDUE: 'überfällig',
    SUPERSEDED_BY_FINAL_INVOICE: 'durch Schlussrechnung abgelöst',
    CANCELLED: 'storniert',
};

const belegName = (b: BelegZustand): string =>
    (b.invoiceNumber || '').trim() || 'Beleg ohne Nummer';

const belegStatusText = (b: BelegZustand): string =>
    BELEG_STATUS_LABEL[(b.status || '').trim()] || (b.status || '').trim() || 'unbekannt';

const istGestellt = (b: BelegZustand): boolean => BELEG_GESTELLT.has((b.status || '').trim());
const istErledigt = (b: BelegZustand): boolean => BELEG_ERLEDIGT.has((b.status || '').trim());
const istStorniert = (b: BelegZustand): boolean => (b.status || '').trim() === 'CANCELLED';
const istSchlussrechnung = (b: BelegZustand): boolean => (b.documentType || '').trim() === 'FINAL_INVOICE';

/**
 * REGEL 3: Schlussrechnung gestellt UND bezahlt?
 *
 * `undefined` = nicht geprueft (Auto-Abschluss), `null` = nicht feststellbar
 * (sperrt), leeres Array = keine Belege im Rechnungsmodul (Altbestand: erlaubt,
 * Regel 1 und 2 tragen allein). Sonst, in der Reihenfolge der Arbeit:
 *
 *   1. Es gibt eine GESTELLTE Schlussrechnung (kein Entwurf, kein Storno).
 *      Fehlt sie, wird ein vorhandener Entwurf beim Namen genannt.
 *   2. Jede gestellte Schlussrechnung ist erledigt — im Regelfall BEZAHLT.
 *   3. Kein anderer gestellter Beleg ist noch offen (Abschlag, Nachtrag).
 */
export const beurteileBelege = (belege: BelegZustand[] | null | undefined): ArchivBlocker[] => {
    if (belege === undefined) return [];
    const blocker = (grund: ArchivBlockGrund, text: string, b?: BelegZustand): ArchivBlocker => ({
        projectId: '',
        projectNumber: null,
        name: null,
        grund,
        text,
        belegId: b?.id,
    });
    if (belege === null) {
        return [blocker('belegstand_unbekannt', 'Die Belege des Projekts konnten nicht geladen werden.')];
    }
    if (belege.length === 0) return [];

    const ergebnis: ArchivBlocker[] = [];
    const schluss = belege.filter(istSchlussrechnung);
    const gestellteSchluss = schluss.filter((b) => istGestellt(b) && !istStorniert(b));
    if (gestellteSchluss.length === 0) {
        const entwurf = schluss.find((b) => !istGestellt(b));
        ergebnis.push(blocker(
            'schlussrechnung_fehlt',
            entwurf
                ? `Schlussrechnung ${belegName(entwurf)} ist noch nicht gestellt (${belegStatusText(entwurf)}).`
                : 'Es ist noch keine Schlussrechnung gestellt.',
            entwurf,
        ));
    }
    gestellteSchluss
        .filter((b) => !istErledigt(b))
        .forEach((b) => ergebnis.push(blocker(
            'schlussrechnung_unbezahlt',
            `Schlussrechnung ${belegName(b)} ist noch nicht bezahlt (${belegStatusText(b)}).`,
            b,
        )));
    belege
        .filter((b) => !istSchlussrechnung(b) && istGestellt(b) && !istErledigt(b))
        .forEach((b) => ergebnis.push(blocker(
            'beleg_offen',
            `Beleg ${belegName(b)} ist noch offen (${belegStatusText(b)}).`,
            b,
        )));
    return ergebnis;
};

/**
 * Wo steht die Schlussrechnung eines BEENDETEN Projekts? Die Antwort fuer die
 * Abrechnungs-Uebersicht: dort steht ein beendetes Projekt auch ohne offenen
 * Betrag, bis ein Administrator es ins Archiv setzt — und ohne diesen Stand
 * sieht die Zeile aus wie ein Fehler.
 *
 * Dieselben Status-Mengen und dieselbe Reihenfolge wie `beurteileBelege`:
 * `erledigt` und `keine_belege` sind genau die Faelle, in denen Regel 3 das
 * Archiv NICHT sperrt. Regel 1 und 2 (offene Posten, Toechter) prueft das
 * hier nicht — der Stand sagt nur, was an den Belegen fehlt.
 */
export type SchlussrechnungsStufe =
    | 'unbekannt'
    | 'keine_belege'
    | 'fehlt'
    | 'entwurf'
    | 'unbezahlt'
    | 'beleg_offen'
    | 'erledigt';

export interface SchlussrechnungsStand {
    stufe: SchlussrechnungsStufe;
    text: string;
}

export const schlussrechnungsStand = (
    belege: BelegZustand[] | null | undefined,
): SchlussrechnungsStand => {
    if (!belege) return { stufe: 'unbekannt', text: 'Belegstand nicht ladbar' };
    if (belege.length === 0) return { stufe: 'keine_belege', text: 'Keine Belege im Rechnungsmodul' };

    const schluss = belege.filter(istSchlussrechnung);
    const gestellteSchluss = schluss.filter((b) => istGestellt(b) && !istStorniert(b));
    if (gestellteSchluss.length === 0) {
        const entwurf = schluss.find((b) => !istGestellt(b));
        return entwurf
            ? { stufe: 'entwurf', text: `Schlussrechnung ${belegName(entwurf)}: ${belegStatusText(entwurf)}` }
            : { stufe: 'fehlt', text: 'Schlussrechnung noch nicht gestellt' };
    }
    const unbezahlt = gestellteSchluss.find((b) => !istErledigt(b));
    if (unbezahlt) {
        return {
            stufe: 'unbezahlt',
            text: `Schlussrechnung ${belegName(unbezahlt)}: ${belegStatusText(unbezahlt)}, Zahlung offen`,
        };
    }
    const offen = belege.find((b) => !istSchlussrechnung(b) && istGestellt(b) && !istErledigt(b));
    if (offen) {
        return { stufe: 'beleg_offen', text: `Beleg ${belegName(offen)} offen (${belegStatusText(offen)})` };
    }
    return {
        stufe: 'erledigt',
        text: `Schlussrechnung ${belegName(gestellteSchluss[0])}: ${belegStatusText(gestellteSchluss[0])}`,
    };
};

/**
 * Status eines GESTELLTEN Belegs, an dem noch Geld aussteht — genau die
 * Belege, die Regel 3 als offen meldet (`beurteileBelege`). Aus beiden Mengen
 * abgeleitet statt aufgezaehlt, damit die Listen und der Riegel nie
 * auseinanderlaufen.
 */
export const BELEG_OFFEN_STATUS: readonly string[] = [...BELEG_GESTELLT].filter((s) => !BELEG_ERLEDIGT.has(s));

/**
 * Stand eines ARCHIVIERTEN Projekts, an dem noch ein Beleg offen ist.
 *
 * Seit dem 15.09.2026 kommt so ein Projekt nicht mehr ins Archiv (Regel 3).
 * Aeltere Archive gibt es aber: 6271 lag seit dem 28.08.2026 mit einer
 * unbezahlten Rechnung im Archiv und war damit aus jeder Liste verschwunden,
 * die offenes Geld zeigt. Bekommt die OFFENEN Belege des Projekts
 * (`ladeOffeneBelegeJeProjekt`); `null` heisst: nichts offen, die Zeile
 * braucht keinen Stand. Texte wie in `schlussrechnungsStand`.
 */
export const offenerBelegStand = (
    belege: BelegZustand[] | null | undefined,
): SchlussrechnungsStand | null => {
    const offen = (belege || []).filter((b) => istGestellt(b) && !istErledigt(b));
    if (offen.length === 0) return null;
    const schluss = offen.find(istSchlussrechnung);
    if (schluss) {
        return {
            stufe: 'unbezahlt',
            text: `Schlussrechnung ${belegName(schluss)}: ${belegStatusText(schluss)}, Zahlung offen`,
        };
    }
    const weitere = offen.length - 1;
    return {
        stufe: 'beleg_offen',
        text: `Beleg ${belegName(offen[0])} offen (${belegStatusText(offen[0])})${weitere > 0 ? ` und ${weitere} weitere` : ''}`,
    };
};

/** Menge im deutschen Zahlenformat; „2" bleibt „2", „2.5" wird „2,5". */
const formatMenge = (wert: number): string =>
    wert.toLocaleString('de-DE', { maximumFractionDigits: 3 });

/** Kopfzeile einer Position: LV-Nummer und Titel, so weit vorhanden. */
const positionsBezeichnung = (p: EigenePosition): string => {
    const nummer = (p.lvPosition || '').trim();
    const titel = (p.title || '').trim();
    if (nummer && titel) return `${nummer} – ${titel}`;
    return nummer || titel || 'Position ohne LV-Nummer und Titel';
};

/**
 * Aus einer offenen Position wird ein benannter Blocker.
 *
 * Die MENGE steht mit drin, nicht nur die Position: „noch etwas offen" laesst
 * offen, ob eine halbe Stunde oder acht Begehungen fehlen — und davon haengt
 * ab, ob der Anwender abrechnet oder verzichtet.
 */
const blockerAusPosition = (p: EigenePosition): ArchivBlocker => {
    const einheit = (p.unit || '').trim();
    const menge = `${formatMenge(p.offeneMenge)}${einheit ? ` ${einheit}` : ''}`;
    return {
        projectId: '',
        projectNumber: null,
        name: null,
        grund: 'eigener_posten',
        serviceId: p.serviceId,
        text: p.zurueckgehalten
            ? `${positionsBezeichnung(p)} — ${menge} erbracht und nicht abgerechnet (Paketposition, ${p.zurueckgehalten}: erst abrechenbar, wenn alle erledigt sind).`
            : `${positionsBezeichnung(p)} — ${menge} erbracht und nicht abgerechnet.`,
    };
};

/**
 * Die offenen Posten eines Projekts aus seinem LV und seinem Protokoll.
 *
 * Drei Abzuege, alle drei aus dem BESTAND und keiner hier neu erfunden:
 *   • Verzicht und Monatspauschale stecken in `openQuantity` (projectLv.ts) —
 *     derselben Funktion, aus der Rechnungsauswahl, Projektcheck und
 *     Rechnungslauf lesen. `flatCoveredBy` muss dafuer gestempelt sein, und
 *     genau das tut `stampFlatCoverage` hier gleich mit: die Deckung wird
 *     ueber die VOLLE Liste aufgeloest (die Pauschalposition deckt andere
 *     Positionen, nicht sich selbst) und laesst sich deshalb nicht je Position
 *     entscheiden.
 *   • Die Alt-Deckung kommt aus `altCoveredQuantities` (externalAdvances) und
 *     ist woertlich der Abzug, mit dem `getProjectFinancials` aus `open`
 *     seinen `displayOpen` macht — inklusive der beiden Deckel: erst
 *     `alt - quantityBilled` (abgerechnete Mengen zehren die aeltesten auf,
 *     und das sind die Alt-Mengen), dann gedeckelt auf das an der Position
 *     ueberhaupt Offene.
 *
 * Dazu, ohne Abzug: PAKETPOSITIONEN mit offenen Bestandteilen. Sie sind nicht
 * abrechenbar (openQuantity 0), aber auch nicht erledigt — ihre gebuchte Menge
 * steht als ZURUECKGEHALTENER Posten in der Liste (heldByComponentsQuantity,
 * das Gegenstueck zu `getProjectFinancials.heldByComponents`).
 *
 * Reihenfolge und Deckel sind bewusst identisch zu `getProjectFinancials`.
 * Wer sie hier verschiebt, laesst die Wache und die Buchhaltungsansicht
 * auseinanderlaufen — und dann streitet ein Anwender mit einer Meldung, die
 * seine eigene Liste nicht bestaetigt.
 */
export const verdichteEigenePosten = (
    services: ServiceItem[] | null | undefined,
    serviceLog: ServiceTransaction[] | null | undefined,
): EigenePosition[] => {
    const liste = stampFlatCoverage([...(services || [])]);
    const altGedeckt = altCoveredQuantities(serviceLog || []);
    const posten: EigenePosition[] = [];
    liste.forEach((s) => {
        if (!s) return;
        // Verzicht und Pauschale sind hier schon abgezogen.
        //
        // PAKETPOSITION: openQuantity sagt bei offenen Bestandteilen 0 — die
        // Menge ist aber nicht erledigt, sondern ZURUECKGEHALTEN, und sperrt
        // das Archiv genauso (Kernregel 4, fail closed: auch ein nicht
        // ladbarer Stand haelt). Beide Groessen schliessen sich aus: wo das
        // Gate sperrt, ist openQuantity 0, und umgekehrt.
        const gehalten = heldByComponentsQuantity(s);
        const offen = gehalten > MENGEN_EPS ? gehalten : openQuantity(s);
        if (offen <= MENGEN_EPS) return;
        const alt = altGedeckt.get(s.id) ?? 0;
        const wirksameAltDeckung = Math.min(Math.max(0, alt - (s.quantityBilled || 0)), offen);
        const rest = offen - wirksameAltDeckung;
        if (rest <= MENGEN_EPS) return;
        posten.push({
            serviceId: s.id,
            lvPosition: s.lvPosition ?? null,
            title: s.title ?? null,
            unit: s.unit ?? null,
            offeneMenge: rest,
            ...(gehalten > MENGEN_EPS ? { zurueckgehalten: bestandteilSperrText(s) } : {}),
        });
    });
    return posten;
};

/**
 * DIE ENTSCHEIDUNG: darf dieses Projekt ins Archiv?
 *
 * Der ganze Riegel — beide Regeln in einem Urteil. Jeder Aufrufer geht hier
 * durch; `beurteileToechter` allein ist nur noch der Teil (1) und kein
 * vollstaendiges Urteil.
 *
 * Die Reihenfolge der Blocker ist die Reihenfolge der Arbeit: erst das Eigene
 * (das liegt in der Hand des Anwenders), dann die Toechter (dort muss er das
 * Projekt wechseln).
 */
export const beurteileArchivierung = (lage: ArchivLage): ArchivUrteil => {
    const blocker: ArchivBlocker[] = [];

    if (!lage.eigenePosten) {
        // Wissensluecke sperrt — dieselbe Richtung wie ueberall in diesem
        // Modul. Eine Archivierung laesst sich nachholen, eine uebersehene
        // Forderung nicht.
        blocker.push({
            projectId: '',
            projectNumber: null,
            name: null,
            grund: 'eigener_stand_unbekannt',
            text: 'Die eigenen Leistungspositionen konnten nicht geladen werden.',
        });
    } else {
        lage.eigenePosten.forEach((p) => blocker.push(blockerAusPosition(p)));
    }

    // REGEL 3 — nur, wenn der Aufrufer die Belege mitgibt (siehe ArchivLage).
    blocker.push(...beurteileBelege(lage.belege));

    const toechterUrteil = beurteileToechter(lage.toechter);
    blocker.push(...toechterUrteil.blocker);

    if (blocker.length === 0) return { erlaubt: true, blocker: [], meldung: '' };
    return { erlaubt: false, blocker, meldung: baueMeldung(blocker) };
};
